import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllSessions } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useNewSessionModal } from '../context/NewSessionModalContext';
import LoadingLeaf from '../components/LoadingLeaf';

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { openNewSession } = useNewSessionModal();

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const typeParam = filter === 'all' ? null : filter;
      const res = await getAllSessions(typeParam);
      const fetched = res.data.sessions || [];
      setSessions(fetched);
      // Auto-select if ?session=<id> was passed (e.g. from dashboard)
      const sessionId = searchParams.get('session');
      if (sessionId) {
        const match = fetched.find(s => s.id === sessionId);
        if (match) setSelectedSession(match);
      }
    } catch (err) {
      setError('Could not load sessions.');
    } finally {
      setLoading(false);
    }
  }, [filter, searchParams]);

  useEffect(() => {
    if (user) fetchSessions();
  }, [user, fetchSessions]);

  const filterCounts = {
    all: sessions.length,
  };

  if (loading) return <LoadingLeaf fullScreen size={72} />;

  if (selectedSession) {
    return (
      <div style={styles.page}>
        <button style={styles.backBtn} onClick={() => setSelectedSession(null)}>← All sessions</button>
        <SessionDetail session={selectedSession} navigate={navigate} />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.pageTitle}>Sessions</div>
          <div style={styles.pageSubtitle}>All recorded sessions across your practice</div>
        </div>
        <button className="tc-btn-primary" style={styles.newBtn} onClick={() => openNewSession()}>
          + New session
        </button>
      </div>

      {error && <div style={styles.errorAlert}>{error}</div>}

      {/* Filter pills */}
      <div style={styles.filterRow}>
        {['all', 'group', 'individual'].map(f => (
          <button
            key={f}
            style={{ ...styles.filterPill, ...(filter === f ? styles.filterPillActive : {}) }}
            onClick={() => { setFilter(f); setSelectedSession(null); }}
          >
            {f === 'all' ? 'All' : f === 'group' ? '◎ Group' : 'Individual'}
          </button>
        ))}
      </div>

      {sessions.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>◎</div>
          <div style={styles.emptyTitle}>No sessions found</div>
          <div style={styles.emptyText}>
            {filter !== 'all' ? `No ${filter} sessions recorded yet.` : 'No sessions recorded yet.'}
          </div>
          {filter === 'all' && (
            <button style={styles.emptyBtn} onClick={() => openNewSession()}>Record first session →</button>
          )}
        </div>
      ) : (
        <div style={styles.list}>
          {sessions.map((s, i) => {
            const isGroup = s.session_type === 'group';
            const participants = s.participant_names?.length > 0
              ? s.participant_names
              : s.clients?.name ? [s.clients.name] : [];
            return (
              <div
                key={s.id}
                style={styles.card}
                className="tc-card-hover"
                onClick={() => setSelectedSession(s)}
              >
                <div style={styles.dateBlock}>
                  <div style={styles.dateDay}>{new Date(s.date + 'T12:00:00').getDate()}</div>
                  <div style={styles.dateMonth}>{new Date(s.date + 'T12:00:00').toLocaleString('default', { month: 'short' })}</div>
                  <div style={styles.dateYear}>{new Date(s.date + 'T12:00:00').getFullYear()}</div>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.cardTitleRow}>
                    <div style={styles.cardTitle}>{s.title || 'Session'}</div>
                    {isGroup && <span style={styles.groupBadge}>◎ Group</span>}
                    {s.group_context?.name && <span style={styles.groupBadge}>🔗 {s.group_context.name} member at the time</span>}
                  </div>
                  <div style={styles.participants}>{participants.join(' · ')}</div>
                  {s.summary?.overview && (
                    <div style={styles.preview}>{s.summary.overview.slice(0, 140)}{s.summary.overview.length > 140 ? '…' : ''}</div>
                  )}
                  <div style={styles.tagRow}>
                    {s.summary?.keyThemes?.slice(0, 3).map((t, i) => <span key={i} style={styles.themeTag}>{t}</span>)}
                    {s.goals?.length > 0 && (
                      <span style={styles.goalTag}>{s.goals.filter(g => g.status === 'met').length}/{s.goals.length} goals</span>
                    )}
                  </div>
                </div>
                <div style={styles.chevron}>→</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SessionDetail({ session, navigate }) {
  const s = session.summary || {};
  const goals = session.goals || [];
  const breakthroughs = session.breakthrough_quotes || [];
  const isGroup = session.session_type === 'group';
  const hasTranscript = session.transcript?.trim().length > 0;
  const participants = session.participant_names?.length > 0
    ? session.participant_names
    : session.clients?.name ? [session.clients.name] : [];

  const downloadTranscript = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([session.transcript], { type: 'text/plain' }));
    a.download = `transcript_${session.title?.replace(/\s+/g, '_') || 'session'}_${session.date}.txt`;
    a.click();
  };

  return (
    <div>
      <div style={detail.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
          <div style={detail.title}>{session.title || 'Session'}</div>
          {isGroup && <span style={detail.groupBadge}>◎ Group session</span>}
          {session.group_context?.name && <span style={detail.groupBadge}>🔗 {session.group_context.name} member at the time</span>}
        </div>
        <div style={detail.date}>
          {new Date(session.date + 'T12:00:00').toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        {participants.length > 0 && (
          <div style={detail.participants}>
            {isGroup ? 'Participants: ' : 'Client: '}{participants.join(' · ')}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
          {!isGroup && session.client_id && (
            <button style={detail.actionBtn} onClick={() => navigate(`/clients/${session.client_id}?tab=sessions`)}>
              View client profile →
            </button>
          )}
          {hasTranscript && <button style={detail.actionBtn} onClick={downloadTranscript}>↓ Transcript</button>}
        </div>
      </div>

      {!session.summary && (
        <div style={detail.alert}>No AI summary was generated for this session.</div>
      )}

      {session.summary && (
        <>
          {s.overview && <div style={detail.card}><div style={detail.sectionTitle}>Overview</div><div style={detail.text}>{s.overview}</div></div>}
          {s.keyThemes?.length > 0 && (
            <div style={detail.card}>
              <div style={detail.sectionTitle}>Key themes</div>
              <div style={detail.tagRow}>{s.keyThemes.map((t, i) => <span key={i} style={detail.tag}>{t}</span>)}</div>
            </div>
          )}
          {s.breakthroughs && <div style={detail.card}><div style={detail.sectionTitle}>Breakthroughs</div><div style={detail.text}>{s.breakthroughs}</div></div>}
          {s.challenges && <div style={detail.card}><div style={detail.sectionTitle}>Challenges</div><div style={detail.text}>{s.challenges}</div></div>}
          {s.clientGrowth && <div style={detail.card}><div style={detail.sectionTitle}>Client growth</div><div style={detail.text}>{s.clientGrowth}</div></div>}
          {s.clinicalObservations && <div style={detail.card}><div style={detail.sectionTitle}>Coaching observations</div><div style={detail.text}>{s.clinicalObservations}</div></div>}
          {s.actionItems?.length > 0 && (
            <div style={detail.card}>
              <div style={detail.sectionTitle}>Action items</div>
              <ul style={detail.list}>{s.actionItems.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </div>
          )}
          {goals.length > 0 && (
            <div style={detail.card}>
              <div style={detail.sectionTitle}>Session goals</div>
              {goals.map((g, i) => {
                const color = g.status === 'met' ? 'var(--sage)' : g.status === 'partial' ? 'var(--gold)' : g.status === 'unmet' ? 'var(--red)' : 'var(--text-faint)';
                const label = g.status === 'met' ? 'Met' : g.status === 'partial' ? 'Partial' : g.status === 'unmet' ? 'Not met' : 'Pending';
                return (
                  <div key={i} style={detail.goalRow}>
                    <div style={detail.goalText}>{g.text}</div>
                    <span style={{ ...detail.goalTag, color, borderColor: color }}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}
          {breakthroughs.length > 0 && (
            <div style={detail.card}>
              <div style={detail.sectionTitle}>Breakthrough moments</div>
              {breakthroughs.map((bq, i) => (
                <div key={i} style={detail.breakthroughCard}>
                  {bq.starred && <span style={detail.star}>★</span>}
                  <div style={detail.quote}>"{bq.quote}"</div>
                  {bq.context && <div style={detail.meta}>{bq.context}</div>}
                </div>
              ))}
            </div>
          )}
          {s.coachRecommendations && <div style={detail.card}><div style={detail.sectionTitle}>Next session recommendations</div><div style={detail.text}>{s.coachRecommendations}</div></div>}
          {s.nextSessionPrompts?.length > 0 && (
            <div style={detail.card}>
              <div style={detail.sectionTitle}>Opening prompts</div>
              {s.nextSessionPrompts.map((p, i) => <div key={i} style={detail.prompt}>{p}</div>)}
            </div>
          )}
          {session.post_reflection && <div style={detail.card}><div style={detail.sectionTitle}>Coach reflection</div><div style={detail.text}>{session.post_reflection}</div></div>}
          {hasTranscript && (
            <div style={detail.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={detail.sectionTitle}>Transcript</div>
                <button style={detail.actionBtn} onClick={downloadTranscript}>↓ Download</button>
              </div>
              <div style={detail.transcript}>{session.transcript.slice(0, 800)}{session.transcript.length > 800 ? '...' : ''}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif", maxWidth: '860px' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
  newBtn: { padding: '11px 20px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '24px' },
  filterPill: { padding: '7px 16px', borderRadius: '20px', border: '1px solid var(--border)', background: 'white', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', fontWeight: '400' },
  filterPillActive: { background: 'var(--mint)', borderColor: 'var(--sage)', color: 'var(--sage)', fontWeight: '600' },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '18px 22px', display: 'flex', alignItems: 'flex-start', gap: '18px', cursor: 'pointer', boxShadow: 'var(--shadow-sm)' },
  dateBlock: { textAlign: 'center', minWidth: '40px', flexShrink: 0 },
  dateDay: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', color: 'var(--sage)', lineHeight: 1 },
  dateMonth: { fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-faint)', marginTop: '2px' },
  dateYear: { fontSize: '10px', color: 'var(--text-faint)', marginTop: '1px' },
  cardBody: { flex: 1, minWidth: 0 },
  cardTitleRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' },
  cardTitle: { fontSize: '15px', fontWeight: '500', color: 'var(--text-dark)' },
  groupBadge: { background: 'rgba(196,149,106,0.12)', color: 'var(--gold)', border: '1px solid rgba(196,149,106,0.35)', padding: '2px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '600' },
  participants: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '6px' },
  preview: { fontSize: '13px', color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: '10px' },
  tagRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  themeTag: { background: 'var(--mint)', color: 'var(--sage)', padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px' },
  goalTag: { background: 'var(--gold-pale)', color: 'var(--gold)', padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px' },
  chevron: { color: 'var(--text-faint)', fontSize: '16px', flexShrink: 0, alignSelf: 'center' },
  backBtn: { background: 'none', border: 'none', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', padding: '0', marginBottom: '28px', fontWeight: '500' },
  emptyState: { textAlign: 'center', padding: '80px 40px' },
  emptyIcon: { fontSize: '32px', color: 'var(--text-faint)', marginBottom: '14px' },
  emptyTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '20px', color: 'var(--text-dark)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '20px' },
  emptyBtn: { background: 'none', border: 'none', color: 'var(--sage)', fontSize: '14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '20px' },
};

const detail = {
  header: { marginBottom: '28px' },
  title: { fontFamily: "'Lora', Georgia, serif", fontSize: '26px', fontWeight: '400', color: 'var(--text-dark)' },
  groupBadge: { background: 'rgba(196,149,106,0.15)', color: 'var(--gold)', border: '1px solid rgba(196,149,106,0.4)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  date: { fontSize: '13px', color: 'var(--text-soft)', marginBottom: '4px' },
  participants: { fontSize: '13px', color: 'var(--text-soft)', fontStyle: 'italic', marginBottom: '4px' },
  actionBtn: { padding: '7px 14px', borderRadius: '8px', background: 'white', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', cursor: 'pointer' },
  alert: { background: '#FAF4EE', border: '1px solid rgba(196,149,106,0.3)', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', fontSize: '14px', color: 'var(--text-mid)' },
  card: { background: 'white', borderRadius: '12px', border: '1px solid var(--border)', padding: '20px 24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '15px', color: 'var(--sage)', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid var(--border-light)' },
  text: { fontSize: '14px', lineHeight: 1.75, color: 'var(--text-mid)' },
  tagRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  tag: { background: 'var(--mint)', color: 'var(--sage)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' },
  list: { paddingLeft: '20px', fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.8 },
  goalRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' },
  goalText: { flex: 1, fontSize: '14px', color: 'var(--text-mid)' },
  goalTag: { padding: '3px 10px', borderRadius: '20px', border: '1px solid', fontSize: '11.5px' },
  breakthroughCard: { background: 'var(--gold-pale)', borderRadius: '8px', padding: '12px 14px', marginBottom: '10px', position: 'relative' },
  star: { position: 'absolute', top: '10px', right: '12px', color: '#C9A84C', fontSize: '14px' },
  quote: { fontSize: '14px', fontStyle: 'italic', color: 'var(--text-dark)', lineHeight: 1.6, marginBottom: '4px' },
  meta: { fontSize: '12px', color: 'var(--text-soft)' },
  prompt: { background: 'var(--gold-pale)', borderLeft: '3px solid var(--gold)', borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: '10px', fontSize: '13.5px', color: 'var(--text-mid)', fontStyle: 'italic' },
  transcript: { background: 'var(--cream)', borderRadius: '8px', padding: '14px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-mid)', lineHeight: 1.7, maxHeight: '300px', overflowY: 'auto' },
};
